import type { ApiClient, UploadErgebnis, VorgangsBezug } from "@kundeninfo/api-client";
import type { ChipProps } from "@kundeninfo/ui/me-design";
import { DOCUMENT_FILE_LIMITS, DOCUMENT_FILE_TEXT as T } from "./texts.js";

/*
 * Das Hochladen am Vorgang ohne Oberfläche — gemeinsam für das Panel „Dateien hochladen“ und das Panel der historischen
 * Vorgänge (Aufgabe 26): Grenzen vor dem Senden, Pakete, die Sätze und die Rangfolge der Ergebnisse je Datei.
 */

/** Ein Ergebnis je Datei, dazu die nachträglich verknüpfte Doublette. */
export type UploadResult = UploadErgebnis | { status: "linked"; name: string };

/** Eine gewählte Datei mit ihrem letzten Ergebnis (`null`: noch nicht gesendet). */
export type UploadRow = { file: File; result: UploadResult | null };

/** Code des Webs (wie der Server): Die Datei ist über der Grenze und geht gar nicht erst hinaus. */
export const TOO_LARGE = "DATEI_ZU_GROSS";
/** Eigener Code des Webs: Die Datei ging nicht hinaus, weil ihr Paket (oder ein früheres) scheiterte. */
const NOT_SENT = "NOT_SENT";

/** Die Sätze der Abweisungen je Code; ein unbekannter Code (Fachfehler einer Datei, W7) heißt „nicht abgelegt“. */
const REJECTION_TEXT: Readonly<Record<string, string>> = {
  [TOO_LARGE]: T.tooLarge, UPLOAD_ABGEBROCHEN: T.aborted, TICKET_LOCK_RETRY: T.lockRetry, [NOT_SENT]: T.notSent,
  ZU_VIELE_DATEIEN: T.tooManyFiles,
};

/** Die Art des Ergebnis-Chips je Status. */
export const RESULT_STATUS_KIND: Record<UploadResult["status"], NonNullable<ChipProps["statusArt"]>> =
  { rejected: "fehler", duplicate: "warnung", created: "ok", linked: "ok" };

/** Das Schlechteste oben (Hausregel): zu groß (ging nie hinaus), abgewiesen, Doublette, abgelegt, noch nicht gesendet.
 *  Die nachträglich verknüpfte Doublette behält den Rang der Doublette — die Zeile springt beim Verknüpfen nicht. */
const RESULT_RANK: Record<UploadResult["status"], number> = { rejected: 1, duplicate: 2, linked: 2, created: 3 };
export const rowRank = (r: UploadRow): number => {
  if (isTooLarge(r.file)) return 0;
  return r.result === null ? 4 : RESULT_RANK[r.result.status];
};

export const isTooLarge = (f: File): boolean => f.size > DOCUMENT_FILE_LIMITS.maxBytes;

export function resultName(r: UploadResult): string {
  return r.status === "created" ? r.file.originalName : r.name;
}

export function resultText(r: UploadResult): string {
  if (r.status === "created") return T.created;
  if (r.status === "linked") return T.linkedExisting;
  if (r.status === "duplicate") return T.duplicate(r.current.originalName);
  return Object.hasOwn(REJECTION_TEXT, r.code) ? REJECTION_TEXT[r.code]! : T.rejected;
}

/** Das Ergebnis einer Datei über der Grenze — sie geht nicht hinaus. */
export const tooLargeResult = (f: File): UploadResult => ({ status: "rejected", code: TOO_LARGE, name: f.name });

/**
 * Sendet Dateien in Paketen zu je `maxFilesPerUpload` nacheinander (K1) — je Datei ein Ergebnis in IHRER Folge. Scheitert
 * ein Paket, stehen es und alle späteren als „nicht gesendet“ da; `error` trägt den Fehlschlag (sonst `null`), `sent`
 * die Zahl der hinausgegangenen Dateien. Dateien über der Grenze filtert der Aufrufer vorher (`isTooLarge`).
 */
export async function sendInPackets(client: Pick<ApiClient, "uploadDocumentFiles">, kundeId: string,
  bezug: VorgangsBezug, files: readonly File[], options: { folderId?: string; fileTypeId?: string } = {},
): Promise<{ results: UploadResult[]; sent: number; error: unknown }> {
  const results: UploadResult[] = [];
  const packet = DOCUMENT_FILE_LIMITS.maxFilesPerUpload;
  let sent = 0;
  try {
    for (; sent < files.length; sent += packet) {
      results.push(...(await client.uploadDocumentFiles(kundeId, bezug, files.slice(sent, sent + packet), options))
        .results);
    }
  } catch (error) {
    results.push(...files.slice(sent).map((f): UploadResult => ({ status: "rejected", code: NOT_SENT, name: f.name })));
    return { results, sent, error };
  }
  return { results, sent: files.length, error: null };
}
